/* eslint-disable react-hooks/exhaustive-deps */
import {
  AutoComplete,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Switch,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useReducer, useState } from "react";
import * as multiDatePicker from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import { v4 as uuidv4 } from "uuid";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { RootState } from "../../../../app/store";
import {
  setCountry,
  setDestination,
  setOrigin,
  setWaypoints,
} from "../../../../features/map/mapSlice";
import {
  serviceTypeListAsync,
  updateServiceTypeListValue,
} from "../../../../features/serviceType/serviceTypeListSlice";
import { AutoCompleteUser } from "../../../../services/userAPI";
import { InlineForm } from "../../../../shared/components/InlineForm";
import { CorporativoDescription } from "../../../../shared/constants/Resources";
import AutoCompleteDTO from "../../../../shared/dtos/autoCompleteDTO";
import { DestinationDTO } from "../../../../shared/dtos/destinationsDTO";
import ServiceTypeDTO from "../../../../shared/dtos/serviceTypeDTO";
import UserRequestDTO from "../../../../shared/dtos/userRequestDTO";
import ScheduleType from "../../../../shared/enum/ScheduleTypeEnum";
import { COUNTRIES } from "../../../../utils/constants/countries";
import { SCHEDULE_TYPES } from "../../../../utils/constants/scheduleTypes";
import { destinationLabel } from "../../../../utils/helpers/destinationLabel";
import ComponentPlacesAutocomplete from "../../../map/PlaceAutocomplete";
import DraggableDestination from "../utils/DraggableDestination";
import { useParams } from "react-router-dom";
import { getUserRequestById } from "../../../../services/userRequestApi";
import { OriginDTO } from "../../../../shared/dtos/originDTO";
import tzlookup from "tz-lookup";
import { CharterDTO } from "../../../charter/list";
import { getSelectableCharters } from "../../../../services/charterApi";

interface reducerAction {
  type: string;
  value?: string;
  data?: any;
  newState?: AutoCompleteDTO;
  fromIndex?: number;
  toIndex?: number;
}

const charterServiceTypeId = +process.env.REACT_APP_CHARTER_SERVICE_TYPE_ID!;

const geoDataToOrigin = (response: google.maps.GeocoderResult): OriginDTO => {
  return {
    id: response?.place_id,
    originLatitude: response?.geometry?.location?.lat(),
    originLongitude: response?.geometry?.location?.lng(),
    originAddress: response?.formatted_address,
  };
};

const UserRequestForm = ({
  form,
  setLoading,
  handleRequestTrip,
}: {
  form: FormInstance<any>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  handleRequestTrip: (userRequest: UserRequestDTO) => Promise<void>;
}) => {
  const dispatch = useAppDispatch();
  const serviceTypeListValue: ServiceTypeDTO[] = useAppSelector(
    (state: RootState) => state.serviceTypeList.value,
  );
  const { distance, destinations, countryName } = useAppSelector(
    (state: RootState) => state?.map,
  );

  const [shouldAutocomplete, setShouldAutocomplete] = useState<boolean>(true);
  const [showRecurrentDays, setShowRecurrentDays] = useState<boolean>(false);
  const [scheduledDatesLength, setScheduledDatesLength] = useState(0);
  const [autoComplete, setAutoComplete] = useState<AutoCompleteDTO[]>([]);
  const [isSelected, setIsSelected] = useState(false);
  const [charters, setCharters] = useState<CharterDTO[]>([]);
  const [charterTimes, setCharterTimes] = useState<number[]>([]);

  const scheduledDates: multiDatePicker.DateObject[] = Form.useWatch(
    "scheduledDates",
    form,
  );

  let { tripId } = useParams();

  const scheduleType: number = Form.useWatch("scheduleType", form);

  const serviceTypeId: number = Form.useWatch("serviceTypeId", form);
  const charterId: number = Form.useWatch("charterId", form);

  const getUserRequestData = async (tripId: number) => {
    await getUserRequestById({ userRequestId: tripId }).then(
      (res: UserRequestDTO) => {
        const geocoder = new window.google.maps.Geocoder();
        const originLatLng = new window.google.maps.LatLng(
          res.originLatitude!,
          res.originLongitude,
        );
        let originAddress = res?.originAddress;

        const country = COUNTRIES.find((x) => x.value === res.countryId);
        dispatch(setCountry(country!));

        geocoder.geocode({ location: originLatLng }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results) {
            const place = results[0];
            if (place) {
              originAddress = place.formatted_address;
              dispatch(setOrigin(geoDataToOrigin(place)));
            }
          }
        });
        const clientFirstName = res.user?.firstName;
        const clientLastName = res.user?.lastName;
        const clientMobile = res.user?.mobile;

        form.setFieldsValue({
          firstName: clientFirstName,
          lastName: clientLastName,
          mobile: clientMobile,
          email: res.user?.email,
          description: res.description,
          passengerName: !!res.passengersData?.length
            ? res.passengersData[0].name
            : `${clientFirstName ?? ""} ${clientLastName ?? ""}`,
          passengerPhone: !!res.passengersData?.length
            ? res.passengersData[0].phone
            : clientMobile,
          reason: res.reason,
          countryId: res.countryId,
          originAddress: originAddress,
          departmentCode: res.departmentCode,
          scheduleType: res.scheduleAt
            ? ScheduleType.SCHEDULED
            : ScheduleType.INSTANT,
          scheduleAt: res.scheduleAt ? dayjs(res.scheduleAt) : null,
          serviceTypeId: res.serviceTypeId,
        });
      },
    );
  };

  useEffect(() => {
    if (tripId) {
      getUserRequestData(+tripId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    dispatch(updateServiceTypeListValue());
  }, [dispatch]);

  useEffect(() => {
    dispatch(serviceTypeListAsync()).then((res: any) => {
      form.setFieldsValue({
        serviceTypeId: res.payload.find(
          (x: any) => x?.name?.toLowerCase() === CorporativoDescription,
        ).id,
      });
    });
  }, [dispatch]);

  useEffect(() => {
    form.setFieldsValue({
      roundDate: null,
      scheduleType: ScheduleType.SCHEDULED,
    });
  }, []);

  useEffect(() => {
    setShowRecurrentDays(false);
    if (scheduleType === ScheduleType.INSTANT)
      form.setFieldsValue({
        scheduleAt: null,
        roundDate: null,
        scheduledDates: null,
        recurrentDays: null,
      });
    else if (scheduleType === ScheduleType.ROUND)
      form.setFieldsValue({
        scheduledDates: null,
        recurrentDays: null,
      });
    else if (scheduleType === ScheduleType.SCHEDULED)
      form.setFieldsValue({
        roundDate: null,
        scheduledDates: null,
        recurrentDays: null,
      });
    else if (scheduleType === ScheduleType.MULTIPLE)
      form.setFieldsValue({
        scheduleAt: null,
        roundDate: null,
        scheduledDates: null,
        recurrentDays: null,
      });
  }, [scheduleType]);

  useEffect(() => {
    form.setFieldsValue({
      countryId: COUNTRIES[0].label,
    });
  }, []);

  const [userData, dispatchUserData] = useReducer(
    (state: AutoCompleteDTO, action: reducerAction) => {
      switch (action.type) {
        case "LAST_NAME":
          return { ...state, lastName: action.value };
        case "FIRST_NAME":
          return { ...state, firstName: action.value };
        case "EMAIL":
          return { ...state, email: action.value };
        case "MOBILE":
          return { ...state, mobile: action.value };
        case "OVERRIDE":
          return { ...state, ...action.newState };
        case "ADD_DESTINATION":
          return {
            ...state,
            destinations: state.destinations.concat({ id: uuidv4() }),
          };
        case "EDIT_DESTINATION":
          return {
            ...state,
            destinations: state.destinations.map((destination) =>
              destination.id !== action.value
                ? destination
                : {
                    ...action.data,
                    id: action.value,
                  },
            ),
          };
        case "REMOVE_DESTINATION":
          form.setFields([
            {
              name: "destinations",
              errors: [],
            },
          ]);
          return {
            ...state,
            destinations: state.destinations.filter(
              (destination) => destination.id !== action.value,
            ),
          };
        case "REORDER_DESTINATIONS":
          const newDestinations = [...state.destinations];
          const [movedDestination] = newDestinations.splice(
            action.fromIndex!,
            1,
          );
          newDestinations.splice(action.toIndex!, 0, movedDestination);
          return {
            ...state,
            destinations: newDestinations,
          };
        default:
          return state;
      }
    },
    { lastName: "initial", destinations: [{ id: uuidv4() }] },
  );

  useEffect(() => {
    const validDestinations = userData.destinations.filter(
      (x) =>
        x &&
        x.directionData &&
        x.directionData.destinationLatitude &&
        x.directionData.destinationLongitude,
    );

    if (validDestinations.length)
      dispatch(
        setWaypoints(
          validDestinations.map((x) => {
            const destination: DestinationDTO = x.directionData;
            return {
              id: destination.id,
              latitude: destination.destinationLatitude,
              address: destination.destinationAddress,
              longitude: destination.destinationLongitude,
            };
          }),
        ),
      );

    dispatch(
      setDestination(
        validDestinations[validDestinations.length - 1]?.directionData,
      ),
    );
  }, [userData.destinations]);

  const onBlurOut = async () => {
    if (shouldAutocomplete) {
      const res: AutoCompleteDTO[] = await AutoCompleteUser(userData);
      if (!res.length) return;
      if (res.length === 1) {
        const user = res[0];
        setIsSelected(true);
        dispatchUserData({ type: "OVERRIDE", newState: user });
        form.setFieldsValue({
          mobile: user.mobile,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
        });
        return;
      }
      setAutoComplete(res);
      setShouldAutocomplete(false);
    }
  };

  const onSelect = (email: string) => {
    const user = autoComplete.find((x) => x.email === email);
    if (!user) return;
    setIsSelected(true);
    dispatchUserData({ type: "OVERRIDE", newState: user });
    form.setFieldsValue({
      mobile: user.mobile,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
    });
  };

  useEffect(() => {
    const length = scheduledDates?.length;
    if (length) {
      setScheduledDatesLength(length);
      if (length > 1 && length > scheduledDatesLength) {
        const secondToLastScheduledDate = scheduledDates[length - 2];
        const lastScheduledDate = scheduledDates[length - 1];
        lastScheduledDate.setHour(secondToLastScheduledDate.hour);
        lastScheduledDate.setMinute(secondToLastScheduledDate.minute);
      }
    }
  }, [scheduledDates]);

  const fetchCharters = async () => {
    if (serviceTypeId === charterServiceTypeId)
      form.setFieldValue("scheduleType", ScheduleType.SCHEDULED);

    if (!charters.length) {
      const selectableCharters = await getSelectableCharters();
      setCharters(selectableCharters);
    }
  };

  useEffect(() => {
    const wasCharterServiceType = form.getFieldValue("charterId");
    form.setFieldValue("charterId", null);

    if (serviceTypeId === charterServiceTypeId || wasCharterServiceType) {
      dispatch(setOrigin(undefined));
      form.setFieldsValue({
        scheduleAt: null,
      });
      setCharterTimes([]);
    }

    fetchCharters();
  }, [serviceTypeId]);

  useEffect(() => {
    if (serviceTypeId === charterServiceTypeId && charterId) {
      const charter = charters.find((x) => x.id === +charterId);

      if (!charter) return;

      dispatch(
        setOrigin({
          id: "",
          originAddress: charter.address!,
          originLatitude: +charter.latitude!,
          originLongitude: +charter.longitude!,
        }),
      );
      form.setFieldsValue({
        originAddress: charter.address!,
        originLatitude: +charter.latitude!,
        originLongitude: +charter.longitude!,
        scheduleAt: null,
      });

      if (!charter.charterTimes?.length) {
        setCharterTimes([]);
        return;
      }

      setCharterTimes(
        charter.charterTimes.map((h) => parseInt(h.time!.split(":")[0], 10)),
      );
    }
  }, [charterId]);

  const onFinish = async (values: any): Promise<void> => {
    setLoading(true);
    const dateList: Array<string> = new Array<string>();

    if (values.scheduledDates) {
      for (var i of values.scheduledDates as multiDatePicker.DateObject[]) {
        i as multiDatePicker.DateObject;
        dateList.push(dayjs(i.toDate()).format("YYYY-MM-DD HH:mm:ss"));
      }
    }

    if (userData.destinations) {
      const checkDestinations = userData.destinations.every(
        (d) =>
          d?.directionData?.destinationLatitude &&
          d?.directionData?.destinationLongitude,
      );
      if (!checkDestinations) {
        form.setFields([
          {
            name: "destinations",
            errors: ["Por favor de llenar los destinos correctamente"],
          },
        ]);
        setLoading(false);
        return;
      }
    }

    try {
      const timezone = tzlookup(
        destinations?.pickUp?.originLatitude!,
        destinations?.pickUp?.originLongitude!,
      );
      handleRequestTrip({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        mobile: values.mobile,
        autoAssign: values.autoAssign ?? true,
        recurrentDays: values.recurrentDays,
        description: values.description,
        reason: values.reason,
        paymentMode: values.paymentMode ?? "MOWI",
        scheduleAt:
          (scheduleType === ScheduleType.SCHEDULED ||
            scheduleType === ScheduleType.ROUND) &&
          values.scheduleAt
            ? dayjs(values.scheduleAt.toDate()).format("YYYY-MM-DD HH:mm:ss")
            : null,
        serviceTypeId: values.serviceTypeId,
        originLatitude: destinations?.pickUp?.originLatitude,
        originLongitude: destinations?.pickUp?.originLongitude,
        destinationLatitude: destinations?.dropOff?.destinationLatitude,
        destinationLongitude: destinations?.dropOff?.destinationLongitude,
        distance: distance ? distance : 0,
        originAddress: destinations?.pickUp?.originAddress,
        destinationAddress: destinations?.dropOff?.destinationAddress,
        destinations: userData.destinations.map((d) => ({
          latitude: d?.directionData?.destinationLatitude,
          longitude: d?.directionData?.destinationLongitude,
          address: d?.directionData?.destinationAddress,
        })),
        scheduledDates:
          scheduleType === ScheduleType.MULTIPLE && dateList ? dateList : null,
        departmentCode: values.departmentCode,
        passengersData:
          values.passengerName && values.passengerPhone
            ? [
                {
                  name: values.passengerName,
                  phone: values.passengerPhone,
                },
              ]
            : [],
        countryId: COUNTRIES.find((x) => x.value === values.countryId)?.value,
        countryName: countryName,
        timezone,
        isAdminTrip: values.showInClosures === true ? false : true,
      });
    } catch (e) {}
  };

  const onScheduleAtChange = () => {
    if (serviceTypeId !== charterServiceTypeId) setShowRecurrentDays(true);
  };

  const moveDestination = (dragIndex: number, hoverIndex: number) => {
    dispatchUserData({
      type: "REORDER_DESTINATIONS",
      fromIndex: dragIndex,
      toIndex: hoverIndex,
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      name="trip"
      id="trip"
      onFinish={onFinish}
      scrollToFirstError
      style={{ maxHeight: "82vh", overflowY: "scroll", overflowX: "hidden" }}
      onKeyDown={(e) => (e.key === "Enter" ? e.preventDefault() : "")}
      initialValues={{
        autoAssign: true,
        showInClosures: false,
      }}
    >
      <InlineForm>
        <Form.Item
          name="firstName"
          label="Nombre"
          tooltip="Cual es su nombre?"
          rules={[
            {
              required: true,
              message: "Por favor inserte su nombre!",
              whitespace: true,
            },
          ]}
        >
          <AutoComplete
            disabled={!!tripId?.length}
            placeholder="Pepe"
            options={autoComplete.map((x) => {
              return {
                label: `${x.firstName} ${x.lastName} - ${x.email}`,
                value: x.email!,
              };
            })}
            optionFilterProp="label"
            value={userData.firstName!}
            searchValue={userData.firstName!}
            onSelect={(e) => onSelect(e)}
            open={!!autoComplete.length && !isSelected}
            onSearch={(e) => {
              dispatchUserData({
                type: "FIRST_NAME",
                value: e,
              });
            }}
            onBlur={onBlurOut}
          />
        </Form.Item>
        <Form.Item
          name="lastName"
          label="Apellido"
          tooltip="Cual es su apellido?"
          rules={[
            {
              required: true,
              message: "Por favor inserte su apellido!",
              whitespace: true,
            },
          ]}
        >
          <AutoComplete
            disabled={!!tripId?.length}
            placeholder="Perez"
            options={autoComplete.map((x) => {
              return {
                label: `${x.firstName} ${x.lastName} - ${x.email}`,
                value: x.email!,
              };
            })}
            optionFilterProp="label"
            onSelect={(e) => onSelect(e)}
            open={false}
            value={userData.lastName!}
            searchValue={userData.lastName!}
            onSearch={(e) => {
              dispatchUserData({
                type: "LAST_NAME",
                value: e,
              });
            }}
            onBlur={onBlurOut}
          />
        </Form.Item>
      </InlineForm>
      <InlineForm>
        <Form.Item
          name="email"
          label="E-mail"
          tooltip="Cual es su correo?"
          rules={[
            {
              type: "email",
              message: "Inserte un correo válido!",
            },
            {
              required: true,
              message: "Inserte su correo!",
            },
          ]}
        >
          <AutoComplete
            disabled={!!tripId?.length}
            options={autoComplete.map((x) => {
              return {
                label: `${x.firstName} ${x.lastName} - ${x.email}`,
                value: x.email!,
              };
            })}
            optionFilterProp="label"
            onSelect={(e) => onSelect(e)}
            placeholder="roge@mowi.com"
            open={false}
            value={userData.email!}
            searchValue={userData.email!}
            onSearch={(e) => {
              dispatchUserData({
                type: "EMAIL",
                value: e,
              });
            }}
            onBlur={onBlurOut}
          />
        </Form.Item>
        <Form.Item
          name="mobile"
          label="Teléfono"
          tooltip="Cual es su teléfono?"
          rules={[
            {
              required: true,
              message: "Por favor inserte su número de teléfono!",
            },
          ]}
        >
          <AutoComplete
            disabled={!!tripId?.length}
            options={autoComplete.map((x) => {
              return {
                label: `${x.firstName} ${x.lastName} - ${x.email}`,
                value: x.email!,
              };
            })}
            optionFilterProp="label"
            onSelect={(e) => onSelect(e)}
            open={false}
            style={{ width: "100%" }}
            placeholder="52013994"
            value={userData.mobile!}
            searchValue={userData.mobile!}
            onSearch={(e) => {
              dispatchUserData({
                type: "MOBILE",
                value: e,
              });
            }}
            onBlur={onBlurOut}
          />
        </Form.Item>
      </InlineForm>
      <InlineForm>
        <Form.Item
          name="passengerName"
          label="Nombre del Pasajero"
          tooltip="Cual es el nombre del Pasajero?"
          rules={[
            {
              required: false,
              message: "Por favor inserte el nombre del Pasajero!",
              whitespace: true,
            },
          ]}
        >
          <Input placeholder="Pepe" />
        </Form.Item>
        <Form.Item
          name="passengerPhone"
          label="Teléfono del Pasajero"
          tooltip="Cual es el teléfono del pasajero? Recuerda no agregar '+', ni espacios. Ejemplo: 59899017660"
          rules={[
            {
              required: false,
              message: "Por favor inserte su nombre!",
              whitespace: true,
            },
          ]}
        >
          <Input placeholder="59899017660" />
        </Form.Item>
      </InlineForm>
      <Form.Item
        name="description"
        label="Descripcion"
        tooltip="Algùn dato de interes?"
      >
        <Input.TextArea showCount maxLength={1000} placeholder="Carga fràgil" />
      </Form.Item>
      <Form.Item name="reason" label="Motivo" tooltip="Motivo del viaje">
        <Input placeholder="Viaje de Trabajo" />
      </Form.Item>

      {serviceTypeListValue && (
        <Form.Item
          name="serviceTypeId"
          label="Tipo Servicio"
          tooltip="En que vehiculo desea viajar?"
          rules={[
            { required: true, message: "Por favor seleccione un servicio!" },
          ]}
        >
          <Select placeholder="Selecione un tipo">
            {serviceTypeListValue.map((serviceType: ServiceTypeDTO) => (
              <Select.Option key={serviceType?.id} value={serviceType?.id}>
                {serviceType?.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {serviceTypeId === charterServiceTypeId && !!charters.length && (
        <Form.Item
          name="charterId"
          label="Direccion de origen"
          tooltip="Lugar de salida"
          rules={
            destinations?.pickUp
              ? [{ required: false }]
              : [
                  {
                    required: true,
                    message: "Inserte una dirección!",
                  },
                ]
          }
        >
          <Select placeholder="Selecione una direccion">
            {charters.map((charter: CharterDTO) => (
              <Select.Option key={charter.id} value={charter.id}>
                {charter.address}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {serviceTypeId !== charterServiceTypeId && (
        <Form.Item
          name="originAddress"
          label="Direccion de origen"
          tooltip="Lugar de salida"
          rules={
            destinations?.pickUp
              ? [{ required: false }]
              : [
                  {
                    required: true,
                    message: "Inserte una dirección!",
                  },
                ]
          }
        >
          <ComponentPlacesAutocomplete type="origin" />
        </Form.Item>
      )}
      <DndProvider backend={HTML5Backend}>
        <Row gutter={[24, 16]} wrap={false}>
          <Col flex="auto">
            <Form.Item
              label="Destinos"
              tooltip="Arrastra para reordenar los destinos"
              required
              validateStatus={
                userData.destinations.length === 0 ||
                userData.destinations.some(
                  (d) =>
                    !d?.directionData?.destinationLatitude ||
                    !d?.directionData?.destinationLongitude,
                )
                  ? "error"
                  : "success"
              }
              help={
                userData.destinations.length === 0 ||
                userData.destinations.some(
                  (d) =>
                    !d?.directionData?.destinationLatitude ||
                    !d?.directionData?.destinationLongitude,
                )
                  ? "Por favor inserte al menos un destino!"
                  : ""
              }
            >
              {userData.destinations.map((destination, index) => {
                return (
                  <DraggableDestination
                    key={destination.id}
                    index={index}
                    id={destination.id}
                    destination={destination}
                    dispatchUserData={dispatchUserData}
                    destinationLabel={destinationLabel(
                      index + 1,
                      userData.destinations?.length,
                    )}
                    moveDestination={moveDestination}
                  />
                );
              })}
            </Form.Item>
          </Col>
          <Col flex="none">
            <Button
              onClick={() =>
                dispatchUserData({
                  type: "ADD_DESTINATION",
                })
              }
              ghost
              type="primary"
            >
              Añadir Destino
            </Button>
          </Col>
        </Row>
      </DndProvider>
      <Form.Item
        name="departmentCode"
        label="Codigo de Departamento"
        tooltip="El viaje tiene algun codigo de departamento?"
      >
        <Input placeholder="CODIGO" />
      </Form.Item>

      <Form.Item
        name="scheduleType"
        label="Tipo de viaje"
        tooltip="Elegir el tipo de viaje"
        rules={[
          { required: true, message: "Por favor seleccione un tipo de viaje!" },
        ]}
      >
        <Select
          placeholder="Seleccione un tipo de viaje"
          options={SCHEDULE_TYPES}
          showSearch
          optionFilterProp="label"
          disabled={serviceTypeId === charterServiceTypeId}
        />
      </Form.Item>

      {scheduleType === ScheduleType.ROUND && (
        <>
          <Form.Item
            name="scheduleAt"
            label="Fecha de ida"
            tooltip="Fecha en la que va a hacer el viaje?"
            rules={[
              {
                required: true,
                message: "Por favor seleccione una fecha de agenda",
              },
            ]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="Fecha"
              style={{ width: "100%" }}
              disabledDate={(current) => {
                return (
                  current && dayjs(current.toDate()) < dayjs().startOf("day")
                );
              }}
              showSecond={false}
            />
          </Form.Item>
          <Form.Item
            name="roundDate"
            label="Fecha de regreso"
            tooltip="Fecha en la que va a hacer el viaje?"
            rules={[
              {
                required: true,
                message: "Por favor seleccione una fecha de regreso",
              },
            ]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="Fecha"
              style={{ width: "100%" }}
              disabledDate={(current) => {
                return (
                  current && dayjs(current.toDate()) < dayjs().startOf("day")
                );
              }}
              showSecond={false}
            />
          </Form.Item>
        </>
      )}

      {scheduleType === ScheduleType.SCHEDULED && (
        <>
          <Form.Item
            name="scheduleAt"
            label="Fecha a agendar"
            tooltip="Fecha en la que va a hacer el viaje?"
            rules={[
              {
                required: true,
                message: "Por favor seleccione una fecha de agenda",
              },
            ]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="Fecha"
              style={{ width: "100%" }}
              onChange={onScheduleAtChange}
              disabledDate={(current) => {
                return (
                  current && dayjs(current.toDate()) < dayjs().startOf("day")
                );
              }}
              disabledTime={() => {
                if (!charterTimes.length) return {};

                return {
                  disabledHours: () =>
                    Array.from({ length: 24 }, (_, i) => i).filter(
                      (h) => !charterTimes.includes(h),
                    ),
                };
              }}
              showSecond={false}
              showMinute={serviceTypeId !== charterServiceTypeId}
            />
          </Form.Item>
        </>
      )}

      {scheduleType === ScheduleType.MULTIPLE && (
        <>
          <Form.Item
            name="scheduledDates"
            label="Fechas a agendar"
            tooltip="En que fechas quiere agendar un viaje?"
            style={{ color: "black" }}
            rules={[
              {
                required: true,
                message: "Por favor seleccione fechas de agenda",
              },
            ]}
          >
            <multiDatePicker.Calendar
              multiple
              plugins={[<TimePicker position="right" hideSeconds />]}
              minDate={dayjs().startOf("day").toDate()}
            />
          </Form.Item>
        </>
      )}
      {showRecurrentDays ? (
        <Form.Item
          name="recurrentDays"
          label="Dias recurrentes"
          tooltip="Que dias de la semana se repetira?"
        >
          <Checkbox.Group
            style={{
              width: "100%",
            }}
          >
            <Checkbox value="1">Lunes</Checkbox>
            <Checkbox value="2">Martes</Checkbox>
            <Checkbox value="3">Miercoles</Checkbox>
            <Checkbox value="4">Jueves</Checkbox>
            <Checkbox value="5">Viernes</Checkbox>
            <Checkbox value="6">Sabado</Checkbox>
            <Checkbox value="7">Domingo</Checkbox>
          </Checkbox.Group>
        </Form.Item>
      ) : null}
      <InlineForm>
        <Form.Item
          name="autoAssign"
          label="Auto-asignar conductor"
          valuePropName="checked"
        >
          <Switch defaultChecked={true} />
        </Form.Item>
        <Form.Item
          name="showInClosures"
          label="Mostrar en cierres"
          valuePropName="checked"
        >
          <Switch defaultChecked={false} />
        </Form.Item>
      </InlineForm>
    </Form>
  );
};

export default UserRequestForm;
